import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, Eye, Edit, Trash2, Plus, FileCheck, PenTool } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { DocumentGenerateDialog } from "./DocumentGenerateDialog";
import { DocumentPreviewModal } from "./DocumentPreviewModal";
import { useNavigate } from "react-router-dom";
import SignatureRequestWizard from "@/components/signatures/SignatureRequestWizard";
import { Layers } from "lucide-react";
import { downloadDocument, hasDownloadablePath } from "@/lib/documentDownload";

interface DocumentsListProps {
  taskId: string;
  unaId: string | null;
}

export function DocumentsList({ taskId, unaId }: DocumentsListProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<any[]>([]);
  const [task, setTask] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<any>(null);
  const [signatureWizardOpen, setSignatureWizardOpen] = useState(false);
  const [selectedDocumentForSignature, setSelectedDocumentForSignature] = useState<any>(null);
  const [phases, setPhases] = useState<any[]>([]);

  useEffect(() => {
    loadTaskAndDocuments();
    loadPhases();
  }, [taskId]);

  const loadPhases = async () => {
    try {
      const { data, error } = await supabase
        .from('task_phases')
        .select('*')
        .eq('task_id', taskId)
        .order('order_number');
      
      if (error) throw error;
      setPhases(data || []);
    } catch (error) {
      console.error('Error loading phases:', error);
    }
  };

  const loadTaskAndDocuments = async () => {
    try {
      // Load task data
      const { data: taskData, error: taskError } = await supabase
        .from('custom_tasks')
        .select('task_title, category_type')
        .eq('id', taskId)
        .single();

      if (taskError) throw taskError;
      setTask(taskData);

      // Load documents
      await loadDocuments();
    } catch (error) {
      console.error('Error loading task:', error);
      toast({
        title: "Error",
        description: "Failed to load task data",
        variant: "destructive",
      });
    }
  };

  const loadDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('generated_documents')
        .select(`
          *,
          template:document_templates(template_name, template_type)
        `)
        .eq('task_id', taskId)
        .order('generated_at', { ascending: false });

      if (error) throw error;
      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast({
        title: "Error",
        description: "Failed to load documents",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (documentId: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const { error } = await supabase
        .from('generated_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;

      toast({
        title: "Document Deleted",
        description: "The document has been removed",
      });

      await loadDocuments();
      await loadPhases();
    } catch (error: any) {
      console.error('Error deleting document:', error);
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive",
      });
    }
  };

  const getPhaseForDocument = (doc: any) => {
    for (const phase of phases) {
      const docReqs = phase.document_requirements || [];
      const matchingReq = docReqs.find((req: any) => 
        req.document_type === doc.document_type || 
        req.generated_document_id === doc.id
      );
      if (matchingReq) {
        return { phase, requirement: matchingReq };
      }
    }
    return null;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      draft: 'bg-gray-500',
      pending_review: 'bg-yellow-500',
      final: 'bg-green-500',
      signed: 'bg-blue-500',
      archived: 'bg-gray-400',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-500';
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getDocumentIcon = (type: string) => {
    const icons = {
      constitution: FileCheck,
      minutes: FileText,
      agreement: FileText,
      procedure: FileText,
      receipt: FileText,
    };
    return icons[type as keyof typeof icons] || FileText;
  };

  if (loading) {
    return <div className="text-center py-8">Loading documents...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Generated Documents ({documents.length})</h3>
        {unaId && (
          <Button onClick={() => setGenerateDialogOpen(true)} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Generate Document
          </Button>
        )}
      </div>

      {documents.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold mb-2">No Documents Yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Generate documents from templates to get started
          </p>
          {unaId && (
            <Button onClick={() => setGenerateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Generate First Document
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {documents.map((doc) => {
            const DocIcon = getDocumentIcon(doc.document_type);
            const phaseInfo = getPhaseForDocument(doc);
            
            return (
              <Card key={doc.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                    <DocIcon className="h-5 w-5 text-primary" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex-1">
                        <h4 className="font-medium truncate">{doc.document_name}</h4>
                        <p className="text-xs text-muted-foreground">
                          {doc.template?.template_name}
                        </p>
                        {phaseInfo && (
                          <div className="flex items-center gap-1 mt-1">
                            <Layers className="h-3 w-3 text-primary" />
                            <span className="text-xs text-primary">
                              Required for Phase {phaseInfo.phase.phase_number}: {phaseInfo.phase.phase_name}
                            </span>
                          </div>
                        )}
                      </div>
                      <Badge className={getStatusColor(doc.status)}>
                        {getStatusLabel(doc.status)}
                      </Badge>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mb-3">
                      <span>v{doc.version}</span>
                      <span>•</span>
                      <span>{format(new Date(doc.generated_at), 'MMM d, yyyy')}</span>
                      {doc.file_size && (
                        <>
                          <span>•</span>
                          <span>{(doc.file_size / 1024).toFixed(1)} KB</span>
                        </>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPreviewDocument(doc)}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                      
                      {doc.status === 'draft' || doc.status === 'pending_review' ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/tasks/${taskId}/documents/${doc.id}/edit`)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                      ) : null}
                      
                      {doc.status === 'final' && !doc.all_signatures_collected && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            setSelectedDocumentForSignature(doc);
                            setSignatureWizardOpen(true);
                          }}
                        >
                          <PenTool className="h-3 w-3 mr-1" />
                          Request Signatures
                        </Button>
                      )}
                      
                      {hasDownloadablePath(doc) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              await downloadDocument(doc);
                            } catch (err: any) {
                              toast({
                                title: 'Download Failed',
                                description: err?.message || 'Could not download document',
                                variant: 'destructive',
                              });
                            }
                          }}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download
                        </Button>
                      )}

                      {doc.all_signatures_collected && hasDownloadablePath({ ...(doc as any), signed_file_path: (doc as any).signed_file_path, signed_file_url: (doc as any).signed_file_url }) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-green-50 hover:bg-green-100 border-green-200"
                          onClick={async () => {
                            try {
                              await downloadDocument(doc as any, { signed: true });
                            } catch (err: any) {
                              toast({
                                title: 'Download Failed',
                                description: err?.message || 'Could not download signed document',
                                variant: 'destructive',
                              });
                            }
                          }}
                        >
                          <Download className="h-3 w-3 mr-1" />
                          Download Signed
                        </Button>
                      )}
                      
                      {doc.status === 'draft' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(doc.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {task && (
        <DocumentGenerateDialog
          open={generateDialogOpen}
          onClose={() => setGenerateDialogOpen(false)}
          taskId={taskId}
          unaId={unaId}
          taskTitle={task.task_title || ''}
          categoryType={task.category_type || 'other'}
          onGenerated={loadDocuments}
        />
      )}

      {previewDocument && (
        <DocumentPreviewModal
          open={!!previewDocument}
          onClose={() => setPreviewDocument(null)}
          document={previewDocument}
          onRequestSignatures={(doc) => {
            setSelectedDocumentForSignature(doc);
            setSignatureWizardOpen(true);
          }}
          onFinalized={() => {
            setPreviewDocument(null);
            loadDocuments();
          }}
        />
      )}

      {selectedDocumentForSignature && unaId && (
        <SignatureRequestWizard
          open={signatureWizardOpen}
          onOpenChange={setSignatureWizardOpen}
          taskId={taskId}
          unaId={unaId}
          preSelectedDocumentId={selectedDocumentForSignature.id}
          onSuccess={() => {
            loadDocuments();
            setSignatureWizardOpen(false);
            setSelectedDocumentForSignature(null);
          }}
        />
      )}
    </div>
  );
}
